/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { TeamButton as TeamButtonComponent } from '@atlassian/teams';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/deprecations/ban-identifiers
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';

type TeamButtonProps = {
	projectId: string;
	isMultiBoard: boolean;
};

export const TeamButton: React.FC<TeamButtonProps> = ({ projectId, isMultiBoard }) => {
	const cloudId = useCloudId();
	const containerId = JiraProjectAri.create({
		siteId: cloudId,
		projectId,
	}).toString();

	// this feature depends on team_and_container_m1, so only show it if it's in the experiment
	// eslint-disable-next-line jira/ff/unsafe-no-exposure, jira/deprecations/ban-identifiers
	const [expConfig] = UNSAFE_noExposureExp('team_and_container_m1');
	if (expConfig.get('isEnabled', null) !== true) {
		return <></>;
	}

	return (
		<JSErrorBoundary
			id="jira-horizontal-nav-header-team-button"
			packageName="jira-horizontal-nav"
			fallback="unmount"
			teamName="Jira Cosmos"
		>
			<ContextualAnalyticsData sourceName="BoardActions" attributes={{ isMultiBoard }}>
				<TeamButtonComponent containerId={containerId} Wrapper={CustomWrapper} />
			</ContextualAnalyticsData>
		</JSErrorBoundary>
	);
};

const CustomWrapper = ({ children }: { children: ReactNode }) => (
	<div css={wrapperStyle}>{children}</div>
);

const wrapperStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-border': Tokens.COLOR_BORDER,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-text-subtle': Tokens.COLOR_TEXT_SUBTLE,
	marginRight: token('space.075'),
});
